import { AppThunk } from "../store";
import { getInsightsAgeOfOpenDefectsData, getInsightsClosedRatioData, getInsightsCumulativeDefectData } from "../../services/DefectInsightsService";
import { setAgeOfOpenDefectsData, setAgeOfOpenDefectsError, setAgeOfOpenDefectsLoading, setClosedRatioData, setClosedRatioError, setClosedRatioLoading, setCumulativeDefectData, setCumulativeDefectError, setCumulativeDefectLoading, setFilteredAgeOfOpenDefectsData, setFilteredClosedRatioData, setFilteredCumulativeDefectData, setFilteredMostCommonDefectsData, setMostCommonDefectsData, setMostCommonDefectsError, setMostCommonDefectsLoading } from "../slices/defectInsightsSlice";
import { selectUserHasCapability, selectUserToken } from "../selectors/userSelectors";
import { selectDefects, selectDefectCodes } from "../selectors/defectSelectors";
import { MostCommonDefectsData } from "../../types/DefectInsightsData";
import { DefectStatus } from "../../types/DefectStatus";
import { Capability } from "../../types/Capability";
import { selectPSC } from "../selectors/pscSelector";
import { selectDefectInsights } from "../selectors/defectInsightsSelectors";
import { DefectDataItem } from "../../types/DefectsDataItem";
import { selectFleetDataItems } from "../selectors/fleetSelectors";

export const fetchInsightsData = (): AppThunk => async (dispatch, getState) => {
    const hasDefectsCapability = selectUserHasCapability(
        getState(),
        Capability.CAP_FLEETAPP_VIEW_DEFECTS
    );

    if (hasDefectsCapability) {
        const insightPromoises = [
            dispatch(fetchClosedRatios()),
            dispatch(fetchCumulativeDefects()),
            dispatch(fetchAgeOfOpenDefects()),
            dispatch(fetchMostCommonDefects()),
        ];

        await Promise.all(insightPromoises);

        dispatch(setupFilteredInsights());
    }
};

export const fetchClosedRatios = (): AppThunk<Promise<boolean>> => async (dispatch, getState) => {
    const userToken = selectUserToken(getState());

    if (userToken) {
        try {
            const response = await getInsightsClosedRatioData(userToken);
    
            if (response?.length) {
                const fleetDataItems = selectFleetDataItems(getState());
                
                const enrichedData = response.map(item => {
                    const vesselData = fleetDataItems.find(fleet => 
                        fleet.vessel?.imo === item.vessel_imo ||
                        fleet.vessel?.imo === item.vessel_imo?.toString()
                    );
                    return {
                        ...item,
                        vessel: vesselData?.vessel.vesselName || item.vessel_imo?.toString() || '',
                        vessel_name: vesselData?.vessel.vesselName || item.vessel_name || item.vessel_imo?.toString() || '',
                        technical_manager: vesselData?.vessel.technicalManager || item.technical_manager,
                        vessel_age: vesselData?.vessel.shipAge || item.vessel_age,
                        vessel_type: vesselData?.vessel.type || item.vessel_type
                    };
                }).filter(item => {
                    const itemDate = new Date(item.date);
                    return itemDate >= cutoffDate;
                }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                
                dispatch(setClosedRatioData(enrichedData));
            } else {
                throw Error('no data');
            }
        } catch (e) {
            console.error(e);
            dispatch(setClosedRatioError(true));
            dispatch(setClosedRatioData([]));
        } finally {
            dispatch(setClosedRatioLoading(false));
        }
    }
    
    return true
};

export const fetchCumulativeDefects = (): AppThunk<Promise<boolean>> => async (dispatch, getState) => {
    const userToken = selectUserToken(getState());

    if (userToken) {
        try {
            const response = await getInsightsCumulativeDefectData(userToken);
    
            if (response?.length) {
                const fleetDataItems = selectFleetDataItems(getState());
                
                const enrichedData = response.map(item => {
                    const vesselData = fleetDataItems.find(fleet => 
                        fleet.vessel?.imo === item.vessel_imo ||
                        fleet.vessel?.imo === item.vessel_imo?.toString()
                    );
                    
                    return {
                        ...item,
                        vessel: vesselData?.vessel.vesselName || item.vessel_imo?.toString() || '',
                        vessel_name: vesselData?.vessel.vesselName || item.vessel_name || item.vessel_imo?.toString() || '',
                        technical_manager: vesselData?.vessel.technicalManager || item.technical_manager,
                        vessel_age: vesselData?.vessel.shipAge || item.vessel_age,
                        vessel_type: vesselData?.vessel.type || item.vessel_type
                    };
                }).filter(item => {
                    const itemDate = new Date(item.date);
                    return itemDate >= cutoffDate;
                }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                
                dispatch(setCumulativeDefectData(enrichedData));
            } else {
                throw Error('no data');
            }
        } catch (e) {
            console.error(e);
            dispatch(setCumulativeDefectError(true));
            dispatch(setCumulativeDefectData([]));
        } finally {
            dispatch(setCumulativeDefectLoading(false));
        }
    }

    return true
};

export const fetchAgeOfOpenDefects = (): AppThunk<Promise<boolean>> => async (dispatch, getState) => {
    const userToken = selectUserToken(getState());
    
    if (userToken) {
        try {
            const response = await getInsightsAgeOfOpenDefectsData(userToken);
    
            if (response?.length) {
                const sortedResponse = [...response].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                dispatch(setAgeOfOpenDefectsData(sortedResponse));
            } else {
                throw Error('no data');
            }
        } catch (e) {
            console.error(e);
            dispatch(setAgeOfOpenDefectsError(true));
            dispatch(setAgeOfOpenDefectsData([]));
        } finally {
            dispatch(setAgeOfOpenDefectsLoading(false));
        }
    }

    return true
};

const cutoffDate = new Date("2025-01-01");
const inspectionHash = new Map<string, boolean>();

function includeDefect(defect:DefectDataItem) {
    if (inspectionHash.has(defect.inspectionId)) {
        return inspectionHash.get(defect.inspectionId);
    }

    let result = false;

    if (defect.inspection.issuedAt) {
        const inspectionDate = new Date(defect.inspection.issuedAt);
    
        if (inspectionDate >= cutoffDate) {
            result = true;
        }

        inspectionHash.set(defect.inspectionId, result);
    }

    return result;
}

export const fetchMostCommonDefects =
    (): AppThunk<Promise<boolean>> => async (dispatch, getState) => {
        try {
            const defects = selectDefects(getState());
            const defectCodes = selectDefectCodes(getState());

            if (defects.length) {
                const mostCommonDefects = defects.reduce(
                    (acc: MostCommonDefectsData[], defect) => {
                        const matchingCode = defectCodes.find((item) => item.code === defect.typeCode);

                        if (
                            defect.typeCode &&
                            !defect.typeCode.includes("00000") &&
                            !defect.typeCode.includes("999999") && 
                            defect.status !== DefectStatus.expired &&
                            defect.gradingAreaField &&
                            includeDefect(defect)
                        ) {
                            acc.push({
                                category: defect.gradingAreaField,
                                type_code: defect.typeCode,
                                short_gen_name: defect.shortGenName ?? "",
                                display_name: matchingCode?.display_name,
                                date: defect.inspectionDate ?? "",
                                technical_manager: defect.technicalManager,
                                vessel: defect.vessel.imo,
                                vessel_age: defect.vessel.shipAge,
                                vessel_name: defect.vesselName,
                                vessel_type: defect.vessel.type
                            });
                        }

                        return acc;
                    },
                    []
                );

                const sortedDefects = [...mostCommonDefects].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                dispatch(setMostCommonDefectsData(sortedDefects));
            } else {
                throw Error("no data");
            }
        } catch (e) {
            console.error(e);
            dispatch(setMostCommonDefectsError(true));
            dispatch(setMostCommonDefectsData([]));
        } finally {
            dispatch(setMostCommonDefectsLoading(false));
        }

        return true
    };

export const setupFilteredInsights = (imo?: string): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const { closedRatioData, cumulativeDefectData, ageOfOpenDefectsData, mostCommonDefectsData } = selectDefectInsights(state);
    const { globalFilteredNames } = selectPSC(state);

    const filteredClosedRatioData = closedRatioData.filter((item) => globalFilteredNames.includes(item.vessel_name));
    const filteredCumulativeDefectData = cumulativeDefectData.filter((item) => 
        globalFilteredNames.includes(item.vessel_name) || globalFilteredNames.includes(item.vessel)
    );
    const filteredAgeOfOpenDefectsData = ageOfOpenDefectsData.filter((item) => globalFilteredNames.includes(item.vessel_name));
    const filteredMostCommonDefectsData = mostCommonDefectsData.filter((item) => globalFilteredNames.includes(item.vessel_name));

    dispatch(setFilteredClosedRatioData(filteredClosedRatioData));
    dispatch(setFilteredCumulativeDefectData(filteredCumulativeDefectData));
    dispatch(setFilteredAgeOfOpenDefectsData(filteredAgeOfOpenDefectsData));
    dispatch(setFilteredMostCommonDefectsData(filteredMostCommonDefectsData));
}